/**
 * Shared "why can't I delete this invoice" reason, used by both
 * InvoiceDetailPage's action menu and InvoiceListPage's row Delete button.
 *
 * Christa-lens redirect (2026-09-15): today she has to open the Delete
 * confirmation and read "This action cannot be undone" before the backend
 * tells her to cancel instead. This disables the affordance up front, with a
 * title explaining why, for every status the backend actually refuses
 * (models/invoices.py::delete_invoice). The backend refusal stays the real
 * guard -- this is only the visible layer over it, so it must stay in sync
 * with that function's own status checks by hand.
 *
 * Codex re-review (2026-09-15), item 4: the backend ALSO refuses to delete
 * any invoice with payment history -- ANY invoice_payments row, active OR
 * voided (InvoiceHasPaymentHistoryError; closes the "void_payment reverts
 * status to draft but the voided row survives" bug). That check runs AFTER
 * the status checks above, so it only ever fires for a status this
 * function would otherwise wave through (draft/ready) -- every other
 * status is already caught by a check above. `hasPaymentHistory` is
 * OPTIONAL and defaults to false: InvoiceListPage's row data has no
 * payment info at all (only amount_paid/payment_date, never a payments
 * list or count -- see schemas.ts's invoiceSchema) and this deliberately
 * does NOT add a new API field just to backfill it there, so its rows
 * fall through to the real backend 409 unchanged, same as before this
 * item. InvoiceDetailPage already loads the full payments list via
 * useInvoicePayments for its Void Payment UI, so it passes the real value.
 */
export function getDeleteBlockedReason(
  status: string | undefined | null,
  hasPaymentHistory: boolean = false,
): string | undefined {
  if (status === 'cancelled') {
    return 'Cancelled invoices are kept for the record.';
  }
  if (status === 'sent' || status === 'overdue') {
    return "Sent invoices can't be deleted. Use Cancel Invoice.";
  }
  if (status === 'paid' || status === 'partially_paid') {
    return 'Paid invoices keep their payment record. Void the payment first.';
  }
  if (hasPaymentHistory) {
    return "This invoice has payment history, so it can't be deleted. It keeps its payment record. Cancel it instead.";
  }
  return undefined;
}
